<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：トラクターヘッドマスタ画面
 *
 * 作成者：2021/11/25 ノア）賀
 * 最終修正者：2022/1/14 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- トラクターヘッド -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.tractorHeadMaster.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('master.tractorHeadMaster.contentTitle') }}</span>
          <span>{{ tableData.length }}</span>
          <span class="viewTitle">{{ $t('master.viewTitle.subject') }}</span>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-form :model="form" ref="form" :rules="rules" :disabled="pageLock">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 226px)"
          border
          highlight-current-row
          :row-class-name="tableRowClassName"
          :stripe="true"
          header-row-class-name="tti-master-table-header"
          :empty-text="$t('messages.common.emptyTableData')"
          @selection-change="handleSelectionChange"
          @sort-change="handleSortChange"
        >
          <el-table-column type="selection" width="35"></el-table-column>
          <!-- No -->
          <el-table-column type="index" :label="$t('master.tractorHeadMaster.no')" width="50" align="center">
          </el-table-column>
          <!-- メーカ -->
          <el-table-column
            class-name="required"
            prop="maker"
            :label="$t('master.tractorHeadMaster.maker')"
            width="500"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].maker'" :rules="rules.maker">
                <el-input
                  :id="'tableData[' + scope.$index + '].maker'"
                  v-model.trim="scope.row.maker"
                  size="small"
                  @change="onInputChange()"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 型式 -->
          <el-table-column
            class-name="required"
            prop="type"
            :label="$t('master.tractorHeadMaster.type')"
            width="500"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].type'" :rules="rules.type">
                <el-input
                  :id="'tableData[' + scope.$index + '].type'"
                  v-model.trim="scope.row.type"
                  size="small"
                  @change="onInputChange()"
                ></el-input>
              </el-form-item>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onDeleteButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.delete)"
          ><tti-svg-icon icon-name="su_013"></tti-svg-icon>{{ $t('buttons.delete') }}</el-button
        >
        <!-- コピー -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onCopyButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.copy)"
        >
          <tti-svg-icon icon-name="su_012"></tti-svg-icon>{{ $t('buttons.copy') }}
        </el-button>
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onInsertButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.insert)"
        >
          <tti-svg-icon icon-name="su_011"></tti-svg-icon>{{ $t('buttons.insert') }}
        </el-button>
        <!-- テンプレート -->
        <el-button
          class="tti-btn-default tti-btn-006"
          @click="onTamplateButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.template)"
        >
          {{ $t('buttons.template') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 入力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onInputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.input)"
        >
          <tti-svg-icon icon-name="su_004"></tti-svg-icon>{{ $t('buttons.input') }}
        </el-button>
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onOutputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
        <!-- file -->
        <input ref="inputFile" type="file" @change="onFileChange" class="fileInput" accept=".csv" />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'
import { findIndex, findLastIndex } from 'lodash'

import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'
import { i18n } from '@/locales'
import { getTractorHeadMasterData, updateTractorHeadMasterData, tractorHeadMasterCSVDownload } from '@/store/apis'
import { TractorHeadMasterData, MasterData } from '@/models'
import { RULES, validateService } from '@/service/validateService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import HelperService from '@/service/helperService'
import { TemplateCode } from '@/commons/constants'

/**
 * トラクターヘッドマスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-tractor-head'
})
export default class MST_TRACTOR_HEAD extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'MST_TRACTOR_HEAD'

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'maker'

  /**
   * 最先のCSVヘッダー
   */
  firstCsvHeader = i18n.t('master.csvHeader.MST_TRACTOR_HEAD').toString()

  /**
   * テンプレートコード
   */
  templateCode = TemplateCode.MST_TRACTOR_HEAD

  /**
   * トラクターヘッドマスタ編集FORM
   */
  form = {
    tableData: this.tableData
  } as Record<string, TractorHeadMasterData[]>

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  /**
   * CSVヘッダーリスト
   */
  csvHeaders = ['maker', 'type']

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // メーカ
      maker: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.tractorHeadMaster.maker')]).toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.tractorHeadMaster.maker').toString()],
          trigger: 'blur'
        },
        {
          validRule: [
            // 50文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.tractorHeadMaster.maker'), 50]).toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.tractorHeadMaster.maker').toString(), 50]
            },
            // 関連重複
            {
              rule: RULES.MUTIPLEREPEAT,
              tableData: this.tableData,
              message: i18n
                .t('vaildate.common.repeat', [
                  i18n.t('master.tractorHeadMaster.maker') + '+' + i18n.t('master.tractorHeadMaster.type')
                ])
                .toString(),
              csvMessage: 'vaildate.csvFile.repeat',
              messageEnterPoint: [
                i18n.t('master.tractorHeadMaster.maker') + '+' + i18n.t('master.tractorHeadMaster.type')
              ],
              multipleRepeatKeyList: ['maker', 'type']
            }
          ],
          trigger: 'blur'
        },
        {
          validator: validateService.getValidator([
            // 複雑さチェック
            {
              rule: RULES.CUSTOM,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              callback: (value: string, rule: any): string | undefined => {
                const currentRowIndex = validateService.getCurrentRowIndex(rule)
                const currentRow = this.visableTableData[currentRowIndex]

                const target = currentRow['maker'] + currentRow['type']
                const isError = this.checkMultipleRepeat(target)

                if (isError) {
                  const message = i18n
                    .t('vaildate.common.repeat', [
                      i18n.t('master.tractorHeadMaster.maker') + '+' + i18n.t('master.tractorHeadMaster.type')
                    ])
                    .toString()
                  return message
                }
              }
            }
          ]),
          trigger: 'blur'
        }
      ],
      // 形式
      type: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.tractorHeadMaster.type')]).toString(),
          csvMessage: 'vaildate.csvFile.required',
          messageEnterPoint: [i18n.t('master.tractorHeadMaster.type').toString()],
          trigger: 'blur'
        },
        {
          validRule: [
            // 50文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 50,
              message: i18n.t('vaildate.common.maxlength', [i18n.t('master.tractorHeadMaster.type'), 50]).toString(),
              csvMessage: 'vaildate.csvFile.maxlength',
              messageEnterPoint: [i18n.t('master.tractorHeadMaster.type').toString(), 50]
            },
            // 関連重複
            {
              rule: RULES.MUTIPLEREPEAT,
              tableData: this.tableData,
              message: i18n
                .t('vaildate.common.repeat', [
                  i18n.t('master.tractorHeadMaster.maker') + '+' + i18n.t('master.tractorHeadMaster.type')
                ])
                .toString(),
              csvMessage: 'vaildate.csvFile.repeat',
              messageEnterPoint: [
                i18n.t('master.tractorHeadMaster.maker') + '+' + i18n.t('master.tractorHeadMaster.type')
              ],
              multipleRepeatKeyList: ['maker', 'type']
            }
          ],
          trigger: 'blur'
        },
        {
          validator: validateService.getValidator([
            // 複雑さチェック
            {
              rule: RULES.CUSTOM,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              callback: (value: string, rule: any): string | undefined => {
                const currentRowIndex = validateService.getCurrentRowIndex(rule)
                const currentRow = this.visableTableData[currentRowIndex]

                const target = currentRow['maker'] + currentRow['type']
                const isError = this.checkMultipleRepeat(target)

                if (isError) {
                  const message = i18n
                    .t('vaildate.common.repeat', [
                      i18n.t('master.tractorHeadMaster.maker') + '+' + i18n.t('master.tractorHeadMaster.type')
                    ])
                    .toString()
                  return message
                }
              }
            }
          ]),
          trigger: 'blur'
        }
      ]
    }
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('trailerMaster > mounted')
    this.initFormTemplate()
    this.fetchTableData()
    this.checkPageLock()
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
    this.initSearchTargets()
  }

  protected fetchTableDataBefore(): () => Promise<MasterData> {
    return getTractorHeadMasterData
  }

  fetchTableDataAfter(): void {
    this.initRule()
    this.initValidate(this.rules)
    this.multipleSelection = []
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCsvRule(): any {
    return this.rules
  }

  /**
   * テーブル項目を再チェックする
   *
   * @returns {void}
   */
  onInputChange(): void {
    this.$nextTick(() => {
      this.formRef.validate()
    })
  }

  /**
   * コピー前の処理。
   *
   * @param {Array<any>} multipleSelection コピー対象
   * @returns {void}
   */
  // eslint-disable-next-line
  beforeDataCopy(multipleSelection: Array<any>): void {
    multipleSelection.forEach(item => {
      delete item.hasTractorHead
    })
  }

  /**
   * 削除前に、削除チェックを行う。
   *
   * @returns {boolean} チェック結果
   */
  deleteTableDataBefore(): boolean {
    let canDelete = true
    for (const data of this.multipleSelection) {
      if (data.hasTractorHead) {
        canDelete = false
        msgBoxService.info(
          i18n
            .t('messages.masterCommon.cantDelete', [
              data.maker + '+' + data.type,
              i18n.t('master.tractorHeadMaster.setedTractor').toString()
            ])
            .toString()
        )
        break
      }
    }

    return canDelete
  }
  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    try {
      const valid = await this.formRef.validate()
      if (!valid) {
        return false
      }

      return valid
    } catch {
      this.formRef.validate((valid, fields) => {
        if (!valid) {
          this.focusOnValidField(fields)
        }
      })
      return false
    }
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    const params = this.getUpsertRequest()
    await updateTractorHeadMasterData(params)
  }

  /**
   * 出力API
   *
   * @returns {void}
   */
  async tableDataOutputAPI(): Promise<void> {
    const res = await tractorHeadMasterCSVDownload()
    HelperService.download(res.fileName, res.data)
  }

  /**
   * 関連重複チェック
   *
   * @param {string} value チェック対象
   * @returns {boolean} true: 関連重複がない
   */
  checkMultipleRepeat(value: string): boolean {
    let error = false
    for (let index = 0; index < this.tableData.length; index++) {
      const first = findIndex(this.tableData, (item: TractorHeadMasterData) => `${item.maker}${item.type}` === value)
      const last = findLastIndex(this.tableData, (item: TractorHeadMasterData) => `${item.maker}${item.type}` === value)
      if (last !== first) {
        error = true
        break
      }
    }

    return error
  }

  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Input,
        field: 'maker'
      },
      {
        type: FormItemType.Input,
        field: 'type'
      }
    ]
  }

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {
    this.formTemplate = {
      maker: '',
      type: ''
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-viewTitle {
  margin-right: 20px;
}

.fileInput {
  display: none;
}
</style>
